// BL-2442: the one URL for a resized copy of a media image, served by Next's image resizer
// (`/_next/image`) on the app's own origin. List thumbnails and the phone app's offline sync
// (BL-2429) share it, so both hit the same resizer cache entry. Pure and client-safe.
// BL-2443: the bidder's cards and photo grid ask for a srcset built from the same URL.

/**
 * The widths the app asks the resizer for. Each must be one of Next's default `imageSizes` /
 * `deviceSizes`, or the route answers 400; the storage gateway's `next-images.test.ts` holds that.
 */
export const RESIZED_IMAGE_WIDTH = {
  /** A list or grid thumbnail (a 40-176px box, sharp at 2x) and the offline cache's copy. */
  listThumbnail: 256,
  /** The lot drawer's Main photo tile, about two thirds of the drawer wide. */
  mainTile: 640,
} as const;

/**
 * The srcset candidates a responsive image offers (BL-2443). Every width must be one of Next's
 * default `imageSizes` / `deviceSizes`; the storage gateway's `next-images.test.ts` holds that.
 */
export const RESIZED_IMAGE_LADDER = {
  /** A card (lot card, auction card) and the phone photo grid: a phone never gets past 828px. */
  card: [384, 640, 828],
  /** The lot page photo grid on a tablet or desktop, where the main tile can be ~1100px wide. */
  gallery: [640, 828, 1080, 1200, 1920],
} as const;

/** A width {@link resizedImageUrl} may ask for. */
export type ResizedImageWidth =
  | (typeof RESIZED_IMAGE_WIDTH)[keyof typeof RESIZED_IMAGE_WIDTH]
  | (typeof RESIZED_IMAGE_LADDER)[keyof typeof RESIZED_IMAGE_LADDER][number];

/** The width a srcset image's plain `src` asks for, used where `srcset` is not honored. */
const SRCSET_FALLBACK_WIDTH = 640;

/**
 * `http://` or `https://` (storage media). A root-relative path is not resizable since BL-2550: the
 * resizer admits only content-hashed `/_next/static/media/` sources (`localPatterns`), which a
 * `next/image` static import builds itself, so a public-folder path would answer 400.
 */
const RESIZABLE = /^https?:\/\//i;

/**
 * A file in the phone app's own storage, served by the app on the page's origin (an offline
 * thumbnail on Android, BL-2609): already list-sized and local, and the resizer can't read it.
 */
const APP_FILE = /^https?:\/\/[^/]+\/_capacitor_file_\//i;

/** @returns whether the resizer can read the source. */
const isResizable = (src: string): boolean => RESIZABLE.test(src) && !APP_FILE.test(src);

/** The resizer quality: Next 16's one default `images.qualities` value. */
export const RESIZED_IMAGE_QUALITY = 75;

/**
 * The resizer URL for a smaller copy of an image: an absolute `http(s):` URL (storage media). Any
 * other source is returned unchanged: a `blob:` URL (an offline cached thumbnail, an upload preview)
 * or a `data:` URL is already in memory, and the resizer refuses both, a file in the phone app's
 * storage (`/_capacitor_file_/`, BL-2609) is local, and a root-relative path is
 * served as it is, because the resizer refuses a public-folder path (BL-2550).
 *
 * @param src - the full-size image's URL.
 * @param options.width - the width to ask for.
 * @returns the URL to put in `<img src>` or to fetch.
 */
export function resizedImageUrl(src: string, options: { width: ResizedImageWidth }): string {
  if (!isResizable(src)) return src;
  return `/_next/image?url=${encodeURIComponent(src)}&w=${options.width}&q=${RESIZED_IMAGE_QUALITY}`;
}

/**
 * The `src` and `srcSet` of a responsive image: one resizer copy per ladder width, so the browser
 * picks the smallest copy that fills the box at the screen's pixel density. A source the resizer
 * cannot read (`blob:`, `data:`) comes back as `{ src }` alone, unchanged.
 *
 * @param src - the full-size image's URL.
 * @param options.widths - the candidates to offer, from {@link RESIZED_IMAGE_LADDER}.
 * @returns the attributes to spread onto an `<img>` or `<source>` (with the caller's `sizes`).
 */
export function resizedImageSrcSet(
  src: string,
  options: { widths: readonly ResizedImageWidth[] },
): { src: string; srcSet?: string } {
  if (!isResizable(src)) return { src };
  return {
    src: resizedImageUrl(src, { width: SRCSET_FALLBACK_WIDTH }),
    srcSet: options.widths.map((width) => `${resizedImageUrl(src, { width })} ${width}w`).join(', '),
  };
}
